import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * NpcServicePattern - Pattern for NPC-provided services (Berry's Restaurant)
 *
 * Handles:
 * - Cost (gold or other resource)
 * - Per-PC usage limits
 * - Effect selection (clear stress, clear HP, gain hope)
 *
 * Config properties used:
 * - cost: { type, amount }
 * - maxUsesPerPC: number
 * - effects: array of { id, label, icon }
 * - descriptionSource: path to description in static data
 */
const NpcServicePattern = ({
  frameId,
  mechanicId,
  config,
  character,
  updateCharacter,
  frameStaticData,
  onClose,
}) => {
  const [selectedEffect, setSelectedEffect] = useState(null);
  const [usesThisSession, setUsesThisSession] = useState(0);

  // Resolve description from static data
  const resolveSource = useCallback((sourcePath) => {
    if (!frameStaticData || !sourcePath) return null;
    return sourcePath.split('.').reduce((obj, key) => obj?.[key], frameStaticData);
  }, [frameStaticData]);

  const description = resolveSource(config.descriptionSource) ||
    'Visit this establishment to recover during downtime.';

  const canUse = usesThisSession < (config.maxUsesPerPC || 2);
  const cost = config.cost || { type: 'gold', amount: 1 };
  const currentGold = character.gold || character.inventory?.gold || 0;
  const canAfford = currentGold >= cost.amount;

  /**
   * Apply the selected effect to the character
   */
  const handleUseService = () => {
    if (!selectedEffect) {
      toast.error('Select an effect first!');
      return;
    }

    if (!canUse) {
      toast.error(`Maximum ${config.maxUsesPerPC} uses per PC!`);
      return;
    }

    if (!canAfford) {
      toast.error(`Not enough ${cost.type}! Need ${cost.amount}.`);
      return;
    }

    // Apply effect based on selection
    let updates = {
      ...character,
    };

    // Deduct cost
    if (cost.type === 'gold') {
      if (character.gold !== undefined) {
        updates.gold = character.gold - cost.amount;
      } else {
        updates.inventory = {
          ...character.inventory,
          gold: (character.inventory?.gold || 0) - cost.amount,
        };
      }
    } else {
      updates[cost.type] = (character[cost.type] || 0) - cost.amount;
    }

    // Apply selected effect
    switch (selectedEffect) {
      case 'clearStress':
        const stressToClear = Math.min(character.stress || 0, 2);
        updates.stress = (character.stress || 0) - stressToClear;
        toast.success(`Cleared ${stressToClear} Stress!`, { icon: '💙' });
        break;

      case 'clearHP':
        const hpToClear = Math.min(character.hp || 0, 2);
        updates.hp = Math.max(0, (character.hp || 0) - hpToClear);
        toast.success(`Healed ${hpToClear} HP!`, { icon: '❤️' });
        break;

      case 'gainHope':
        const hopeToGain = Math.min(6 - (character.hope || 0), 1);
        updates.hope = (character.hope || 0) + hopeToGain;
        toast.success(`Gained ${hopeToGain} Hope!`, { icon: '✨' });
        break;

      default:
        toast.error('Unknown effect');
        return;
    }

    updateCharacter(updates);
    setUsesThisSession(prev => prev + 1);
    setSelectedEffect(null);
  };

  const effects = config.effects || [
    { id: 'clearStress', label: 'Clear Stress', icon: '💙' },
    { id: 'clearHP', label: 'Clear HP', icon: '❤️' },
    { id: 'gainHope', label: 'Gain Hope', icon: '✨' },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-amber-600 dark:text-amber-400 mb-1">
          🍽️ Berry's Restaurant
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {description}
        </p>
      </div>

      {/* Cost & Limits Info */}
      <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex justify-between items-center mb-2">
          <span className="text-amber-800 dark:text-amber-200 font-medium">
            Cost: {cost.amount} {cost.type}
          </span>
          <span className="text-amber-700 dark:text-amber-300 text-sm">
            Uses: {usesThisSession}/{config.maxUsesPerPC || 2}
          </span>
        </div>
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Current {cost.type}: {currentGold}
        </p>
      </div>

      {/* Effect Selection */}
      <div className="space-y-2">
        <h4 className="font-medium text-gray-700 dark:text-gray-300">
          Choose a benefit:
        </h4>
        <div className="grid grid-cols-1 gap-2">
          {effects.map((effect) => (
            <DomainCardButton
              key={effect.id}
              onClick={() => setSelectedEffect(effect.id)}
              variant={selectedEffect === effect.id ? 'active' : 'secondary'}
              className="justify-start text-left"
            >
              <span className="text-xl mr-2">{effect.icon}</span>
              <span>{effect.label}</span>
            </DomainCardButton>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <DomainCardButton
          onClick={handleUseService}
          disabled={!selectedEffect || !canUse || !canAfford}
          variant="primary"
          className="flex-1"
        >
          {!canAfford
            ? `Need ${cost.amount} ${cost.type}`
            : !canUse
            ? 'Max uses reached'
            : 'Order Food'}
        </DomainCardButton>
        <DomainCardButton onClick={onClose} variant="secondary">
          Leave
        </DomainCardButton>
      </div>

      {/* Status Messages */}
      {!canUse && (
        <p className="text-center text-amber-600 dark:text-amber-400 text-sm">
          You've eaten here enough for now. Come back next session!
        </p>
      )}
    </div>
  );
};

export default NpcServicePattern;
